import React, { useState } from 'react';
import {
  UserIcon,
  ClockIcon,
  InboxStackIcon,
  ChatBubbleLeftRightIcon,
  FolderOpenIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';

// Componente Tooltip para explicações
const InfoTooltip = ({ text }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-block ml-1">
      <InformationCircleIcon
        className="w-4 h-4 text-gray-400 hover:text-primary-600 cursor-help transition-colors"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      />
      {showTooltip && (
        <div className="absolute z-50 w-64 p-3 text-sm text-white bg-gray-900 rounded-lg shadow-lg -left-32 top-6 animate-fadeIn">
          <div className="relative">
            {text}
            <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 -top-4 left-1/2"></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Função para formatar minutos
const formatMinutes = (minutes) => {
  if (!minutes || isNaN(minutes)) return '-';
  if (minutes < 60) return `${Math.round(minutes)}min`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hours}h ${mins}min`;
};

// Função para obter cor do status
const getStatusColor = (status) => {
  switch (status) {
    case 'critical':
      return { border: 'border-red-500', bg: 'bg-red-500', text: 'text-red-500', bgLight: 'bg-red-50' };
    case 'warning':
      return { border: 'border-yellow-500', bg: 'bg-yellow-500', text: 'text-yellow-500', bgLight: 'bg-yellow-50' };
    case 'ok':
    default:
      return { border: 'border-green-500', bg: 'bg-green-500', text: 'text-green-500', bgLight: 'bg-green-50' };
  }
};

// Função para obter ícone do status
const StatusIcon = ({ status, className = '' }) => {
  const baseClass = `${className}`;
  switch (status) {
    case 'critical':
      return <ExclamationCircleIcon className={`${baseClass} text-red-500`} />;
    case 'warning':
      return <ExclamationTriangleIcon className={`${baseClass} text-yellow-500`} />;
    case 'ok':
    default:
      return <CheckCircleIcon className={`${baseClass} text-green-500`} />;
  }
};

// Função para obter cor da disponibilidade
const getAvailabilityColor = (status) => {
  switch (status) {
    case 'online':
      return 'bg-green-500';
    case 'busy':
      return 'bg-yellow-500';
    case 'offline':
    default:
      return 'bg-gray-400';
  }
};

const AgentProductivityCard = ({ agent, thresholds }) => {
  const statusColors = getStatusColor(agent.overall_status);

  return (
    <div className={`bg-white rounded-lg shadow-sm border-2 ${statusColors.border} overflow-hidden`}>
      {/* Header */}
      <div className={`px-4 py-3 ${statusColors.bgLight} border-b`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-gray-600" />
              </div>
              <div
                className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${getAvailabilityColor(agent.availability_status)}`}
                title={agent.availability_status}
              />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm truncate max-w-32" title={agent.agent_name}>
                {agent.agent_name}
              </h3>
              <p className="text-xs text-gray-500 capitalize">
                {agent.availability_status}
              </p>
            </div>
          </div>
          <StatusIcon status={agent.overall_status} className="w-6 h-6" />
        </div>
      </div>

      {/* Métricas */}
      <div className="px-4 py-3 space-y-3">
        {/* TME */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-gray-600">
            <ClockIcon className="w-4 h-4" />
            <span className="text-sm">TME</span>
            <InfoTooltip text="Tempo Médio de Espera: tempo médio que o agente demora para dar a primeira resposta. Calculado sobre TODAS as conversas do agente (histórico geral)" />
          </div>
          <div className="flex items-center gap-2">
            <StatusIcon status={agent.response_time_status} className="w-4 h-4" />
            <span className={`text-sm font-medium ${
              agent.response_time_status === 'critical' ? 'text-red-600' :
              agent.response_time_status === 'warning' ? 'text-yellow-600' : 'text-gray-900'
            }`}>
              {formatMinutes(agent.avg_response_time_minutes)}
            </span>
          </div>
        </div>

        {/* Pendentes de Retorno */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-gray-600">
            <InboxStackIcon className="w-4 h-4" />
            <span className="text-sm">Pendentes</span>
            <InfoTooltip text="Conversas aguardando resposta do agente AGORA. Cliente enviou última mensagem e agente ainda não respondeu. Crítico ≥5, Atenção ≥3" />
          </div>
          <div className="flex items-center gap-2">
            <StatusIcon status={agent.pending_status} className="w-4 h-4" />
            <span className={`text-sm font-medium ${
              agent.pending_status === 'critical' ? 'text-red-600' :
              agent.pending_status === 'warning' ? 'text-yellow-600' : 'text-gray-900'
            }`}>
              {agent.pending_return_count || 0}
            </span>
          </div>
        </div>

        {/* Atividades Hoje */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-gray-600">
            <BoltIcon className="w-4 h-4" />
            <span className="text-sm">Reabertas (Hoje)</span>
            <InfoTooltip text="Conversas reabertas hoje atribuídas a este agente" />
          </div>
          <span className="text-sm font-medium text-gray-900">
            {agent.reopened_today || 0}
          </span>
        </div>

        {/* Volume Hoje */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-gray-600">
            <ChatBubbleLeftRightIcon className="w-4 h-4" />
            <span className="text-sm">Criadas (Hoje)</span>
            <InfoTooltip text="Total de conversas criadas/recebidas hoje por este agente" />
          </div>
          <span className="text-sm font-medium text-gray-900">
            {agent.volume_today || 0}
          </span>
        </div>

        {/* Total Abertos */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-gray-600">
            <FolderOpenIcon className="w-4 h-4" />
            <span className="text-sm">Total Abertos</span>
            <InfoTooltip text="Total de conversas abertas + pendentes do agente AGORA" />
          </div>
          <span className="text-sm font-medium text-gray-900">
            {agent.total_open || 0}
          </span>
        </div>
      </div>

      {/* Footer com indicadores */}
      <div className="px-4 py-2 bg-gray-50 border-t flex items-center justify-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-gray-500">Crítico</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-yellow-500" />
          <span className="text-gray-500">Atenção</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-gray-500">OK</span>
        </div>
      </div>
    </div>
  );
};

const AgentProductivityCards = ({
  data = {},
  loading = false,
}) => {
  const { summary = {}, agents = [], thresholds = {} } = data;

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-500">Carregando produtividade...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header com resumo */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              Produtividade dos Agentes
            </h2>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-gray-600">Crítico: {summary.critical || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-gray-600">Atenção: {summary.warning || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-gray-600">OK: {summary.ok || 0}</span>
            </div>
            <div className="border-l pl-4">
              <span className="text-gray-600">
                Total Pendentes: <strong className="text-red-600">{summary.total_pending_return || 0}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* Thresholds info */}
        <div className="mt-2 text-xs text-gray-500">
          Limites: TME &gt; {thresholds.sla_minutes || 5}min = Crítico |
          Pendentes &gt;= {thresholds.pending_threshold || 5} = Crítico
        </div>
      </div>

      {/* Grid de cards */}
      {agents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Nenhum agente encontrado
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {agents.map((agent) => (
            <AgentProductivityCard
              key={agent.agent_id}
              agent={agent}
              thresholds={thresholds}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentProductivityCards;
